import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { PDFGenerationOverlay } from '../components/PDFGenerationOverlay';
import { honorairesAPI, rapportsAPI } from '../services/api';
import Plate from '../components/Plate';
import { playSound } from '../utils/SoundPlayer';
import { Save, ArrowLeft, ArrowRight, FileDown, Calculator, Search, X, Plus } from 'lucide-react';
import toast from 'react-hot-toast';


const HonoraireForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const isEditing = id && id !== 'nouvelle' && id !== 'nouveau';
    const rapportIdParam = searchParams.get('rapport');

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [rapport, setRapport] = useState(null);

    // Rapport Search Modal
    const [showRapportModal, setShowRapportModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [pdfStatus, setPdfStatus] = useState('idle');

    const [form, setForm] = useState({
        rapport_id: rapportIdParam || '',
        statut: 'emis',
        date_emission: new Date().toISOString().split('T')[0],
        honoraire_fixe: 0,
        frais_dossier: 150,
        tarif_photos: 40,
        deplacement: 0,
        nb_photos: 0,
        frais_photos: 0,
        tva_pct: '19',
        tva_montant: 0,
        montant_ht: 0,
        montant_ttc: 0,
        mode_paiement: '',
        date_paiement: '',
        numero_quittance: '',
        observations: '',
    });

    // Logic calculation same as Python code provided:
    // hon_fix = calculate_value(total_base)
    // tva_amount = (tva_pct / 100) * hon_fix
    // frais_photos = nb_photos * tarif_ph
    // total_global = hon_fix + frais_dos + deplacement + tva_amount + frais_photos
    useEffect(() => {
        const honFix = parseFloat(form.honoraire_fixe) || 0;
        const tvaPct = parseFloat(form.tva_pct) || 0;
        const fraisDossier = parseFloat(form.frais_dossier) || 0;
        const tarifPhotos = parseFloat(form.tarif_photos) || 0;
        const deplacement = parseFloat(form.deplacement) || 0;
        const nbPhotos = parseInt(form.nb_photos) || 0;

        const fraisPhotos = nbPhotos * tarifPhotos;
        const tvaAmount = (tvaPct / 100) * honFix;
        const totalHT = honFix + fraisDossier + deplacement + fraisPhotos;
        const totalTTC = totalHT + tvaAmount;

        setForm(prev => ({
            ...prev,
            frais_photos: fraisPhotos,
            tva_montant: tvaAmount.toFixed(2),
            montant_ht: totalHT.toFixed(2),
            montant_ttc: totalTTC.toFixed(2),
        }));
    }, [form.honoraire_fixe, form.tva_pct, form.frais_dossier, form.tarif_photos, form.deplacement, form.nb_photos]);

    // Calculation logic matching the user's provided python script
    const calculateHonFixe = (base) => {
        const A2 = parseFloat(base) || 0;
        if (A2 < 0) return 0;
        let result = 1000;

        if (A2 <= 10000) {
            result = 1000;
        } else if (A2 <= 50000) {
            result = 1000 + (A2 - 10000) * (1550 - 1000) / (50000 - 10000);
        } else if (A2 <= 100000) {
            result = 1550 + (A2 - 50000) * (1830 - 1550) / (100000 - 50000);
        } else if (A2 <= 250000) {
            result = 1830 + (A2 - 100000) * (2700 - 1830) / (250000 - 100000);
        } else if (A2 <= 500000) {
            result = 2700 + (A2 - 250000) * (4000 - 2700) / (500000 - 250000);
        } else if (A2 <= 750000) {
            result = 4000 + (A2 - 500000) * (5252 - 4000) / (750000 - 500000);
        } else if (A2 <= 1000000) {
            result = 5252 + (A2 - 750000) * (6560 - 5252) / (1000000 - 750000);
        } else {
            result = 7450;
        }

        return Math.round(result * 100) / 100;
    };

    useEffect(() => {
        if (isEditing) {
            setLoading(true);
            honorairesAPI.getById(id)
                .then(res => {
                    const h = res.data?.data;
                    if (!h) throw new Error('Note non trouvée');
                    const initNbPhotos = h.nb_photos || h.rapport_nb_photos || 0;
                    const initTarifPhotos = h.tarif_photos || 40;
                    const initFraisPhotos = parseFloat(h.frais_photos) > 0 ? parseFloat(h.frais_photos) : (initNbPhotos * initTarifPhotos);

                    const baseHonFixe = calculateHonFixe(h.total_moeuvre || 0);
                    const honFixCalculated = baseHonFixe > 0 ? baseHonFixe : parseFloat(h.honoraire_fixe || 0);

                    setForm({
                        rapport_id: h.rapport_id || '',
                        numero_rapport: h.numero_rapport || '',
                        numero_honoraire: h.numero_honoraire || '',
                        numero_sinistre: h.numero_sinistre || '',
                        nom_assure: h.nom_assure || '',
                        nom_assurance: h.nom_assurance || '',
                        immatriculation: h.immatriculation || '',
                        marque_vehicule: h.marque_vehicule || '',
                        modele_vehicule: h.modele_vehicule || '',
                        statut: h.statut || 'emis',
                        date_emission: h.date_emission ? h.date_emission.split('T')[0] : '',
                        honoraire_fixe: honFixCalculated || 0,
                        frais_dossier: h.frais_dossier || 150,
                        tarif_photos: initTarifPhotos,
                        deplacement: h.deplacement || 0,
                        nb_photos: initNbPhotos,
                        frais_photos: initFraisPhotos,
                        tva_pct: h.tva_pct || '19',
                        tva_montant: h.tva_montant || '',
                        montant_ht: h.montant_ht || '',
                        montant_ttc: h.montant_ttc || '',
                        mode_paiement: h.mode_paiement || '',
                        date_paiement: h.date_paiement ? h.date_paiement.split('T')[0] : '',
                        numero_quittance: h.numero_quittance || '',
                        observations: h.observations || '',
                    });

                    if (h.rapport_id) {
                        rapportsAPI.getById(h.rapport_id)
                            .then(rRes => {
                                const r = rRes.data?.data;
                                if (r) {
                                    setRapport(r);
                                    setForm(prev => {
                                        const nb_photos = (prev.nb_photos === 0 || !prev.nb_photos) && r.nb_photos > 0 ? r.nb_photos : prev.nb_photos;
                                        const honoraire_fixe = (prev.honoraire_fixe === 0 || !prev.honoraire_fixe) && r.total_general > 0 ? calculateHonFixe(r.total_general) : prev.honoraire_fixe;
                                        return { ...prev, nb_photos, honoraire_fixe };
                                    });
                                }
                            })
                            .catch(() => console.error("Could not fetch linked rapport"));
                    }
                })
                .catch(() => toast.error('Note non trouvée'))
                .finally(() => setLoading(false));
        } else if (rapportIdParam) {
            rapportsAPI.getById(rapportIdParam)
                .then(res => {
                    const r = res.data?.data;
                    if (r) {
                        setRapport(r);
                        setForm(prev => ({
                            ...prev,
                            rapport_id: r.id,
                            nb_photos: r.nb_photos || (r.photos ? r.photos.length : 0),
                            honoraire_fixe: calculateHonFixe(r.total_general),
                        }));

                        // Check if this specific rapport already has a note
                        honorairesAPI.list({ search: rapportIdParam })
                            .then(hRes => {
                                const existing = hRes.data?.data?.find(h =>
                                    h.source_table === 'notes_honoraires' &&
                                    (h.rapport_id === parseInt(rapportIdParam) || h.numero_rapport === r.numero_rapport)
                                );
                                if (existing) {
                                    toast.error(`Une note d'honoraire existe déjà pour ce rapport (${existing.numero_honoraire}).`);
                                    navigate(`/honoraires/${existing.id}`);
                                }
                            })
                            .catch(() => { });
                    }
                })
                .catch(() => { });
        }
    }, [id, isEditing, rapportIdParam, navigate]);

    // Rapport search logic
    useEffect(() => {
        if (!showRapportModal) return;
        if (searchQuery.length < 2) {
            setSearchResults([]);
            return;
        }

        const timer = setTimeout(async () => {
            setSearching(true);
            try {
                const res = await rapportsAPI.list({ search: searchQuery, limit: 10 });
                setSearchResults(res.data?.data || []);
            } catch (err) {
                console.error(err);
            } finally {
                setSearching(true); // Wait, searching should be false
                setSearching(false);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery, showRapportModal]);

    const handleSelectRapport = async (r) => {
        try {
            // Check if this specific rapport already has a note
            const hRes = await honorairesAPI.list({ search: r.id });
            const existing = hRes.data?.data?.find(h =>
                h.source_table === 'notes_honoraires' &&
                (h.rapport_id === r.id || h.numero_rapport === r.numero_rapport)
            );

            if (existing) {
                toast.error(`Rapport ${r.numero_rapport} a déjà une Note (${existing.numero_honoraire})`);
                if (window.confirm(`Ce rapport a déjà una Note d'honoraire (${existing.numero_honoraire}). Voulez-vous la consulter ?`)) {
                    navigate(`/honoraires/${existing.id}`);
                    setShowRapportModal(false);
                }
                return;
            }

            setRapport(r);
            setForm(prev => ({
                ...prev,
                rapport_id: r.id,
                nb_photos: r.nb_photos || (r.photos ? r.photos.length : 0),
                honoraire_fixe: calculateHonFixe(r.total_general),
            }));
            setShowRapportModal(false);
            toast.success(`Rapport ${r.numero_rapport} lié !`);
        } catch (err) {
            console.error(err);
        }
    };

    const handleSubmit = async (statut) => {
        if (!form.montant_ht) {
            toast.error('Le montant HT est obligatoire');
            return;
        }
        setSaving(true);
        try {
            const data = { ...form, statut };
            if (isEditing) {
                await honorairesAPI.update(id, data);
                toast.success('Note d\'honoraire mise à jour');
                playSound('save');
                navigate('/honoraires');
            } else {

                const res = await honorairesAPI.create(data);
                toast.success('Note d\'honoraire créée — N°: ' + res.data.data.numero_honoraire);
                playSound('save');
                navigate(`/honoraires/${res.data.data.id}`);

            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Erreur lors de la sauvegarde';
            toast.error(msg);
            if (err.response?.data?.existingId) {
                // If the error contains existingId, we redirect there after a delay
                setTimeout(() => navigate(`/honoraires/nh-${err.response.data.existingId}`), 2000);
            }
        } finally { setSaving(false); }
    };

    const handleGeneratePDF = async () => {
        if (!id) return toast.error('Sauvegardez d\'abord la note');
        try {
            setPdfStatus('generating');
            const res = await honorairesAPI.generatePDF(id);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `Honoraire_${id}.pdf`; a.click();
            URL.revokeObjectURL(url);

            setPdfStatus('success');
            setTimeout(() => setPdfStatus('idle'), 1500);
        } catch {
            setPdfStatus('idle');
            toast.error('Erreur PDF', { id: 'pdf' });
        }
    };

    const formatCurrency = (v) => v ? new Intl.NumberFormat('fr-DZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + ' DZD' : '0,00 DZD';

    if (loading) return <Layout title="Chargement..."><div className="loading-spinner"><div className="spinner" /></div></Layout>;

    return (
        <Layout title={isEditing ? `Modifier Note d'Honoraire ${form.numero_honoraire || ''}` : 'Nouvelle Note d\'Honoraire'}>
            <PDFGenerationOverlay status={pdfStatus} />
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                {/* Header */}
                <div className="flex-between mb-4">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}><ArrowLeft size={16} /></button>
                        <div>
                            <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--primary-800)' }}>
                                {isEditing ? `Modifier la Note d'Honoraire ${form.numero_honoraire || ''}` : 'Nouvelle Note d\'Honoraire'}
                            </h2>
                            {rapport && (
                                <p style={{ fontSize: '12px', color: 'var(--gold-500)' }}>
                                    Rapport: {rapport.numero_rapport} — {rapport.nom_assure}
                                </p>
                            )}
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                        {!rapport && !isEditing && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                background: 'rgba(200, 164, 55, 0.05)',
                                padding: '6px 12px',
                                borderRadius: '100px',
                                border: '1px dashed rgba(200, 164, 55, 0.3)',
                                animation: 'fadeIn 1s ease',
                                marginRight: '4px'
                            }}>
                                <span style={{
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    color: 'var(--gold-600)',
                                    textTransform: 'uppercase',
                                    whiteSpace: 'nowrap'
                                }}>
                                    Selectionnez
                                </span>
                                <div style={{ animation: 'pointingArrow 1.2s ease-in-out infinite', color: 'var(--gold-500)', display: 'flex' }}>
                                    <ArrowRight size={18} strokeWidth={3} />
                                </div>
                            </div>
                        )}

                        {!isEditing && (
                            <button
                                className="btn plus-btn-animated"
                                onClick={() => setShowRapportModal(true)}
                                style={{
                                    borderRadius: '12px',
                                    padding: '10px 20px',
                                    background: 'linear-gradient(135deg, #c8a437 0%, #ecd68b 50%, #c8a437 100%)',
                                    color: 'var(--primary-900)',
                                    border: '1px solid rgba(0,0,0,0.1)',
                                    animation: 'premiumPulse 3s ease-in-out infinite',
                                    fontWeight: '800',
                                    boxShadow: '0 4px 15px rgba(200, 164, 55, 0.4)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                <Plus size={16} strokeWidth={2.5} /> {rapport ? 'Changer Rapport/PV' : 'Rapport/PV'}
                            </button>
                        )}
                        {id && <button className="btn btn-pdf btn-sm" onClick={handleGeneratePDF}><FileDown size={14} /> PDF</button>}
                        <button className="btn btn-primary btn-sm" onClick={() => handleSubmit(form.statut)} disabled={saving}>
                            {saving ? 'Sauvegarde...' : <><Save size={14} /> Sauvegarder</>}
                        </button>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: '24px', alignItems: 'start' }}>
                    {/* Left Column: Forms */}
                    <div className="form-left-column">
                        {/* Rapport/Legacy Reference */}
                        {(rapport || (id && id.startsWith('eth-'))) && (
                            <div style={{ background: 'var(--gold-100)', border: '1px solid var(--gold-300)', borderRadius: 'var(--radius)', padding: '12px 16px', marginBottom: '16px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                                {[
                                    { label: 'Rapport', value: rapport?.numero_rapport || form.numero_rapport || 'LEGACY' },
                                    { label: 'Sinistre', value: rapport?.numero_sinistre || form.numero_sinistre },
                                    { label: 'Assuré', value: rapport?.nom_assure || form.nom_assure },
                                    { label: 'Véhicule', value: rapport ? `${rapport.marque_vehicule || ''} ${rapport.modele_vehicule || ''}` : `${form.marque_vehicule || ''} ${form.modele_vehicule || ''}` },
                                    { label: 'Immat.', value: rapport?.immatriculation || form.immatriculation, isPlate: true },
                                    { label: 'Total Rapport', value: rapport ? formatCurrency(rapport.total_general || rapport.total_moeuvre) : '—' },
                                ].map(item => (
                                    <div key={item.label}>
                                        <div style={{ fontSize: '10px', color: '#8a6c00', textTransform: 'uppercase', fontWeight: '700' }}>{item.label}</div>
                                        <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--primary-800)' }}>
                                            {item.isPlate && item.value ? <Plate value={item.value} /> : (item.value || '—')}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Form Sections */}
                        <div className="card" style={{ marginBottom: '16px' }}>
                            <div className="form-section-title">📋 Informations Générales</div>
                            <div className="form-grid form-grid-2">
                                <div className="form-group">
                                    <label className="form-label">Date d'Émission *</label>
                                    <input type="date" className="form-control" value={form.date_emission}
                                        onChange={e => setForm(p => ({ ...p, date_emission: e.target.value }))} required />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Statut</label>
                                    <select className="form-control" value={form.statut} onChange={e => setForm(p => ({ ...p, statut: e.target.value }))}>
                                        <option value="brouillon">Brouillon</option>
                                        <option value="emis">Émis</option>
                                        <option value="paye">Payé</option>
                                        <option value="annule">Annulé</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="card" style={{ marginBottom: '16px' }}>
                            <div className="form-section-title"><Calculator size={14} style={{ display: 'inline', marginRight: '6px' }} />Calcul des Honoraires</div>
                            <div className="form-grid form-grid-2">
                                <div className="form-group">
                                    <label className="form-label">Honoraires Fixes (DA) <span style={{ fontSize: '10px', color: '#888', fontWeight: 'normal' }}>(Calcul Auto)</span></label>
                                    <input type="number" className="form-control" value={form.honoraire_fixe}
                                        disabled style={{ backgroundColor: 'var(--gray-50)', color: 'var(--gray-500)', cursor: 'not-allowed' }}
                                        onChange={e => setForm(p => ({ ...p, honoraire_fixe: e.target.value }))} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">TVA (%)</label>
                                    <input type="number" className="form-control" value={form.tva_pct}
                                        onChange={e => setForm(p => ({ ...p, tva_pct: e.target.value }))} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Frais de Dossier (DA)</label>
                                    <input type="number" className="form-control" value={form.frais_dossier}
                                        onChange={e => setForm(p => ({ ...p, frais_dossier: e.target.value }))} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Déplacement (DA)</label>
                                    <input type="number" className="form-control" value={form.deplacement}
                                        onChange={e => setForm(p => ({ ...p, deplacement: e.target.value }))} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Tarif Photos (DA)</label>
                                    <input type="number" className="form-control" value={form.tarif_photos}
                                        onChange={e => setForm(p => ({ ...p, tarif_photos: e.target.value }))} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Nombre de Photos</label>
                                    <input type="number" className="form-control" value={form.nb_photos}
                                        onChange={e => setForm(p => ({ ...p, nb_photos: e.target.value }))} />
                                </div>
                            </div>
                        </div>

                        <div className="card" style={{ marginBottom: '16px' }}>
                            <div className="form-section-title">💳 Informations de Paiement</div>
                            <div className="form-grid form-grid-2">
                                <div className="form-group">
                                    <label className="form-label">Mode de Paiement</label>
                                    <select className="form-control" value={form.mode_paiement} onChange={e => setForm(p => ({ ...p, mode_paiement: e.target.value }))}>
                                        <option value="">Sélectionner...</option>
                                        <option value="cheque">Chèque</option>
                                        <option value="virement">Virement Bancaire</option>
                                        <option value="especes">Espèces</option>
                                        <option value="autre">Autre</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Date de Paiement</label>
                                    <input type="date" className="form-control" value={form.date_paiement}
                                        onChange={e => setForm(p => ({ ...p, date_paiement: e.target.value }))} />
                                </div>
                                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                    <label className="form-label">N° Quittance / Référence</label>
                                    <input type="text" className="form-control" value={form.numero_quittance} placeholder="Référence du paiement"
                                        onChange={e => setForm(p => ({ ...p, numero_quittance: e.target.value }))} />
                                </div>
                            </div>
                        </div>

                        <div className="card" style={{ marginBottom: '24px' }}>
                            <div className="form-section-title">📝 Observations</div>
                            <textarea className="form-control" rows={3} value={form.observations} placeholder="Observations particulières..."
                                onChange={e => setForm(p => ({ ...p, observations: e.target.value }))} />
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginBottom: '40px' }}>
                            <button className="btn btn-ghost" onClick={() => navigate(-1)}>Annuler</button>
                            <button className="btn btn-outline" onClick={() => handleSubmit('brouillon')} disabled={saving}>
                                <Save size={15} /> Brouillon
                            </button>
                            {id && (
                                <button className="btn btn-pdf" onClick={handleGeneratePDF}>
                                    <FileDown size={15} /> Générer PDF
                                </button>
                            )}
                            <button className="btn btn-primary" onClick={() => handleSubmit(form.statut)} disabled={saving}>
                                {saving ? 'Sauvegarde...' : <><Save size={15} /> Sauvegarder</>}
                            </button>
                        </div>
                    </div>

                    {/* Right Column: Totals Summary */}
                    <div className="form-right-column" style={{ position: 'sticky', top: '100px' }}>
                        <div style={{
                            background: 'var(--primary-900)',
                            borderRadius: '24px',
                            padding: '30px',
                            boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
                            border: '1px solid rgba(255,255,255,0.1)'
                        }}>
                            <div style={{ fontSize: '14px', fontWeight: '800', color: 'var(--gold-400)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '25px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '10px' }}>
                                Résumé du Calcul
                            </div>
                            <div style={{ display: 'grid', gap: '18px' }}>
                                {[
                                    { label: 'Honoraires Fixes', value: form.honoraire_fixe },
                                    { label: 'Frais de Dossier', value: form.frais_dossier },
                                    { label: 'Frais de Photos', value: form.frais_photos, sub: `${form.nb_photos} x ${form.tarif_photos} DA` },
                                    { label: 'Frais de Déplacement', value: form.deplacement },
                                    { label: `TVA (${form.tva_pct}%)`, value: form.tva_montant },
                                ].map(item => (
                                    <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div>
                                            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', fontWeight: '500' }}>{item.label}</div>
                                            {item.sub && <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '10px' }}>{item.sub}</div>}
                                        </div>
                                        <span style={{ color: 'white', fontWeight: '700', fontSize: '14px' }}>{formatCurrency(parseFloat(item.value) || 0)}</span>
                                    </div>
                                ))}

                                <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', margin: '15px 0' }} />

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', fontWeight: '600' }}>TOTAL HT</span>
                                    <span style={{ color: 'white', fontWeight: '800', fontSize: '18px' }}>{formatCurrency(parseFloat(form.montant_ht) || 0)}</span>
                                </div>
                                <div style={{
                                    background: 'linear-gradient(135deg, rgba(200, 164, 55, 0.1) 0%, transparent 100%)',
                                    borderRadius: '16px',
                                    padding: '20px',
                                    marginTop: '10px',
                                    border: '1px solid rgba(200, 164, 55, 0.2)'
                                }}>
                                    <span style={{ color: 'var(--gold-300)', fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>
                                        Net à Payer (TTC)
                                    </span>
                                    <span style={{ color: 'var(--gold-400)', fontSize: '28px', fontWeight: '900', letterSpacing: '-1px' }}>
                                        {formatCurrency(parseFloat(form.montant_ttc) || 0)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal: Sélection Rapport */}
            {showRapportModal && (
                <div className="modal-overlay" style={{ display: 'flex', backdropFilter: 'blur(8px)', background: 'rgba(15, 23, 42, 0.6)' }} onClick={() => setShowRapportModal(false)}>
                    <div className="modal-content" style={{ width: '1000px', maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', borderRadius: '32px', border: '1px solid rgba(255,255,255,0.2)', boxShadow: '0 50px 100px -20px rgba(0,0,0,0.5)', overflow: 'hidden', background: 'var(--surface-1)' }} onClick={e => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="modal-header" style={{ background: 'linear-gradient(135deg, var(--gold-500) 0%, var(--gold-700) 100%)', padding: '30px 40px', borderBottom: 'none', position: 'relative', overflow: 'hidden' }}>
                            <div style={{ position: 'absolute', top: '-50px', right: '-30px', width: '150px', height: '150px', background: 'rgba(255,255,255,0.2)', borderRadius: '50%' }} />
                            <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <div>
                                    <h3 style={{ fontSize: '24px', fontWeight: '900', color: 'var(--primary-900)', letterSpacing: '-0.5px', margin: 0 }}>Rechercher un Rapport</h3>
                                    <p style={{ color: 'rgba(10, 22, 40, 0.6)', fontSize: '14px', marginTop: '4px', fontWeight: '600' }}>Liez le rapport pour générer la facturation</p>
                                </div>
                                <button className="btn-close" onClick={() => setShowRapportModal(false)} style={{ background: 'rgba(0,0,0,0.1)', color: 'var(--primary-900)', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none' }}>
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        {/* Search Section */}
                        <div style={{ padding: '30px 40px', background: 'white' }}>
                            <div style={{ position: 'relative', boxShadow: '0 10px 30px rgba(var(--primary-rgb), 0.08)', borderRadius: '20px' }}>
                                <input type="text" className="form-control" placeholder="N° Rapport, Nom de l'Assuré, Immatriculation..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus style={{ height: '64px', fontSize: '17px', paddingLeft: '60px', borderRadius: '20px', border: '2px solid var(--surface-3)', fontWeight: '600' }} />
                                <div style={{ position: 'absolute', left: '22px', top: '50%', transform: 'translateY(-50%)', color: 'var(--gold-500)' }}><Search size={24} strokeWidth={2.5} /></div>
                                {searching && <div style={{ position: 'absolute', right: '22px', top: '50%', transform: 'translateY(-50%)' }}><div className="spinner-primary" style={{ width: '24px', height: '24px' }} /></div>}
                            </div>
                        </div>

                        {/* Results Section */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '0 40px 40px', background: 'white' }}>
                            {searchResults.length > 0 ? (
                                <div style={{ display: 'grid', gap: '16px' }}>
                                    {searchResults.map(r => (
                                        <div key={r.id} className="dossier-result-premium" onClick={() => handleSelectRapport(r)} style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 120px', alignItems: 'center', padding: '20px 25px', background: 'var(--surface-1)', borderRadius: '20px', border: '1px solid var(--surface-3)', cursor: 'pointer' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <div style={{ fontSize: '16px', fontWeight: '800', color: 'var(--primary-900)' }}>{r.nom_assure || '—'}</div>
                                                <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--gold-600)' }}>{r.numero_rapport || '—'}</div>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Sinistre</div>
                                                <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--primary-600)' }}>{r.numero_sinistre || '—'}</div>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Total</div>
                                                <div style={{ fontSize: '14px', fontWeight: '800', color: 'var(--success)' }}>{formatCurrency(r.total_general)}</div>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <button className="btn btn-primary btn-sm" style={{ borderRadius: '12px', fontWeight: '800' }}>Lier</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '100px 0' }}>
                                    <div style={{ color: 'var(--surface-4)', marginBottom: '20px' }}><Search size={64} style={{ opacity: 0.2 }} /></div>
                                    <h4 style={{ fontWeight: '800', color: 'var(--primary-900)' }}>{searchQuery.length < 2 ? 'Rechercher un rapport' : 'Aucun rapport trouvé'}</h4>
                                    <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>{searchQuery.length < 2 ? "Saisissez le numéro de rapport ou le nom de l'assuré." : `Désolé, nous n'avons nada pour "${searchQuery}".`}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default HonoraireForm;
